'use client';

import type React from 'react';

import { Button } from '@components/components/ui/button';
import { AlertCircle, Inbox, MousePointerClick } from 'lucide-react';

interface EventEmptyStateProps {
  type: 'loading' | 'empty' | 'error' | 'no-selection';
  message?: string;
  onRetry?: () => void;
}

export const EventEmptyState: React.FC<EventEmptyStateProps> = ({
  type,
  message,
  onRetry,
}) => {
  if (type === 'loading') {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-shade-teal-500" />
          <span className="text-sm">{message || 'Loading events...'}</span>
        </div>
      </div>
    );
  }

  if (type === 'error') {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 max-w-sm text-center px-4">
          <AlertCircle className="h-8 w-8 text-red-400" />
          <p className="text-sm text-gray-600">
            {message || 'Something went wrong'}
          </p>
          {onRetry && (
            <Button
              variant="link"
              size="sm"
              onClick={onRetry}
              className="text-shade-teal-600 hover:text-shade-teal-700"
            >
              Try again
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (type === 'empty') {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Inbox className="h-8 w-8" />
          <p className="text-sm">{message || 'No events found'}</p>
        </div>
      </div>
    );
  }

  // no-selection
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-gray-400 max-w-md text-center px-4">
        <MousePointerClick className="h-8 w-8" />
        <p className="text-sm leading-relaxed">
          {message || 'Select an event to view details'}
        </p>
      </div>
    </div>
  );
};
